import {
    federalElectionData,
    FederalElectionDataType,
    getSpecificStateElectionData,
    InterknowlogyData,
    InterknowlogyExtendedParty,
    RegisteredElectionState,
    StateElectionDataType,
} from '.'

// Election Data for the Federal and State Elections in Australia Below:

export type ElectionData = FederalElectionDataType | StateElectionDataType
export const coalitionName = 'Coalition'

export const isFederalElection = (
    electionData: ElectionData,
): electionData is FederalElectionDataType => {
    return electionData.type === 'federal'
}
export const isStateElection = (
    electionData: ElectionData,
): electionData is StateElectionDataType => {
    // Update with any other state elections here
    return electionData.type === 'state-wa'
}

export const getFederalElectionData = (): ElectionData => federalElectionData
export const getStateElectionData = (
    state: RegisteredElectionState,
): ElectionData => getSpecificStateElectionData(state)

// Top Party Data for the provided Election Data

export interface TopPartyDataProps {
    electionData: ElectionData
    partiesToShow: number
    dataFeed: InterknowlogyData
    sortByPredicted?: boolean
}
export const getTopPartyData = (props: TopPartyDataProps): TopPartyResults => {
    const { electionData } = props

    // TODO: Breakdown, is this necessary?
    // if (isFederalElection(electionData)) {
    //     return getFederalElectionPartyData({
    //         ...props,
    //         electionData,
    //     })
    // } else if (isStateElection(electionData)) {
    //     return getStateElectionPartyData({
    //         ...props,
    //         electionData,
    //     })
    // } else {
    //     throw new Error('Invalid Election Data')
    // }

    return getElectionPartyData(props)
}

export interface TopParty {
    code: string
    name: string
    seats: number
    seatsPredicted: number
    color: string
    predictedColor: string
}
export interface CombinedTopParty extends TopParty {
    combinedPartyCodes: string[]
}
export interface TopPartyResults {
    parties: TopParty[]
    sort: 'ascending' | 'descending'
}

interface PartyColors {
    [key: string]: string
}
export const primaryPartyColors: PartyColors = {
    COL: '#0008E1', // Coalition
    LNP: '#0008E1', // Liberal-National Coalition - Used for test-data
    ALP: '#E10004', // Labor
    GRN: '#76B500', // Greens
    IND: '#00CBBD', // Independent
    OTH: '#8E6B8F', // Other
}
export const secondaryPartyColors: PartyColors = {
    COL: '#0008E133', // Coalition
    LNP: '#0008E133', // Liberal-National Coalition - Used for test-data
    ALP: '#E1000433', // Labor
    GRN: '#76B50033', // Greens
    IND: '#00CBBD33', // Independent
    OTH: '#8E6B8F33', // Other
}

export const getElectionPartyData = ({
    partiesToShow,
    dataFeed,
    sortByPredicted = true,
}: TopPartyDataProps): TopPartyResults => {
    const sort = 'descending'

    // TODO: Do we need to filter to the specific area
    const area = dataFeed.areas[0]

    const coalitionPartyResults = getCombinedTopParty(
        'COL',
        'Coalition',
        area.parties,
        (party) =>
            party.combinedPartyCodes &&
            party.isCoalition &&
            party.combinedCode === 'COL',
        {
            primary: primaryPartyColors.COL,
            secondary: secondaryPartyColors.COL,
        },
        true,
    )
    // If results were found, let's clear out the other coalition parties
    if (coalitionPartyResults) {
        area.parties = coalitionPartyResults.parties.filter(
            (party) => !party.isCoalition,
        )
    }
    // Combine all the other parties into the party code provided!
    const otherPartyResults = getCombinedTopParty(
        'OTH',
        'Other',
        area.parties,
        (party) =>
            party.partyCode === 'OTH' ||
            Object.keys(primaryPartyColors).indexOf(party.partyCode) === -1,
        {
            primary: primaryPartyColors.OTH,
            secondary: secondaryPartyColors.OTH,
        },
        true,
    )
    if (otherPartyResults) {
        area.parties = otherPartyResults.parties
    }

    // Now let's map all the other parties, yay!
    const mappedParties: TopParty[] = area.parties.map((party) => {
        return {
            code: party.partyCode,
            name: party.shortPartyName ?? party.partyName,
            seats: party.seatsWon,
            seatsPredicted: party.seatsWon + party.seatsAhead,
            color: primaryPartyColors[party.partyCode] || '#000000',
            predictedColor:
                secondaryPartyColors[party.partyCode] || '#00000033',
        }
    })

    // Now add the extra data, if needed!
    if (coalitionPartyResults) {
        mappedParties.push(coalitionPartyResults.topParty)
    }
    if (otherPartyResults) {
        mappedParties.push(otherPartyResults.topParty)
    }

    // Now lets sort and return the data!
    const parties = mappedParties
        .sort((a, b) => sortTopPartyResults(sort, sortByPredicted, a, b))
        .slice(0, partiesToShow)

    return {
        parties: parties,
        sort: sort,
    }
}

export const getCombinedTopParty = (
    partyCode: string,
    partyName: string,
    parties: InterknowlogyExtendedParty[],
    predicate: (
        value: InterknowlogyExtendedParty,
        index: number,
        obj: InterknowlogyExtendedParty[],
    ) => unknown,
    colors: { primary: string; secondary: string },
    clear?: boolean,
):
    | { topParty: CombinedTopParty; parties: InterknowlogyExtendedParty[] }
    | undefined => {
    const topParty: CombinedTopParty = {
        code: partyCode,
        name: partyName,
        seats: 0,
        seatsPredicted: 0,
        color: colors.primary,
        predictedColor: colors.secondary,
        combinedPartyCodes: [],
    }

    parties.filter(predicate).forEach((party) => {
        topParty.seats += party.seatsWon
        topParty.seatsPredicted += party.seatsWon + party.seatsAhead
        topParty.combinedPartyCodes.push(party.partyCode)
    })

    // if we're clearing, then run this!
    if (clear === true) {
        parties = parties.filter((party) => !predicate(party, 0, parties))
    }

    if (topParty.combinedPartyCodes.length > 0) {
        // Results found, return them!
        return {
            parties,
            topParty,
        }
    }

    return undefined
}

export const sortTopPartyResults = (
    sort: string,
    sortByPredicted: boolean,
    a: TopParty,
    b: TopParty,
): number => {
    const aSeat = sortByPredicted ? a.seatsPredicted : a.seats
    const bSeat = sortByPredicted ? b.seatsPredicted : b.seats

    return sort === 'descending' ? bSeat - aSeat : aSeat - bSeat
}
